/**
 * Bound an awaited promise so the router `beforeEach` guard can never hang.
 *
 * Why this exists (DPT-252 / fix-noauth-login-timeout):
 * The guard `await`s several backend calls before it calls `next()` — the auth
 * check (token refresh), the cross-region probe, the search-field catalog and
 * ACL hydration. A plain `.catch()` only handles a *rejection*; a request that
 * never settles (cold/slow API, stalled refresh) leaves the `await` pending
 * forever, so `next()` is never called, the SPA never mounts a route, and the
 * user is stuck on an endless in-app spinner that only "clear site data" fixes
 * (because that drops the token so the guard takes the synchronous no-auth path
 * and skips the hanging network call).
 *
 * Wrapping each awaited call with this guarantees it always settles within
 * `ms`, so the guard always reaches a `next()`.
 *
 * Resolves to `fallback` if the promise rejects OR does not settle within `ms`;
 * otherwise resolves to the promise's value. It never rejects.
 *
 * @param promise   the work to bound
 * @param ms        timeout budget in milliseconds
 * @param fallback  value to resolve with on timeout or rejection
 * @param onTimeout optional hook fired only when the timeout (not a rejection)
 *                  wins the race — used by the caller to log which call stalled
 */
export function withGuardTimeout<T>(
  promise: Promise<T>,
  ms: number,
  fallback: T,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;

    const finish = (value: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      // Fire the diagnostic hook before resolving so logs reflect a true stall.
      if (onTimeout) onTimeout();
      finish(fallback);
    }, ms);

    Promise.resolve(promise).then(
      (value) => finish(value),
      // Swallow rejection — the caller already decided `fallback` is the safe
      // outcome. Rejection logging stays at the call site (chained `.catch`).
      () => finish(fallback),
    );
  });
}
